import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SEGMENT_LIMITS } from "../constants";
import {
  findOverlapGroups,
  mergeOverlapGroups,
} from "../overlaps";
import type { FormCondition, FormRule, SegmentFormValues } from "../schemas";
import type { useRestoreOrder } from "./useRestoreOrder";
import { useUndoToast } from "./useUndoToast";

import type {
  OverlapGroup,
  OverlapMark,
  OverlapSource,
  SegmentField,
} from "@/features/segments/types";
import useToast from "@/lib/hooks/useToast";

type UseOverlapMergeParams = {
  fields: SegmentField[];
  enabled: boolean;
  removeRules: (indexes: number[]) => void;
  insertRule: (index: number, rule: FormRule) => void;
  restoreOrder: ReturnType<typeof useRestoreOrder>;
};

type MergeSnapshot = {
  survivors: Map<FormCondition["clientKey"], FormCondition>;
  mergedAway: { ruleKey: FormRule["clientKey"]; condition: FormCondition }[];
  removedRules: FormRule[];
  countBefore: number;
};

export function useOverlapMerge({
  fields,
  enabled,
  removeRules,
  insertRule,
  restoreOrder,
}: UseOverlapMergeParams) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const showUndoToast = useUndoToast();
  const {
    watch,
    getValues,
    setValue,
    trigger,
    formState: { isSubmitted },
  } = useFormContext<SegmentFormValues>();
  const latest = useRef({ isSubmitted, enabled });

  useEffect(() => {
    latest.current = { isSubmitted, enabled };
  });

  const rules = watch("rules");
  const groups = enabled ? findOverlapGroups(rules, watch("type"), fields) : [];
  const conditionByKey = new Map(
    rules.flatMap((rule) =>
      rule.conditions.map((condition) => [condition.clientKey, condition] as const),
    ),
  );
  const overlapByCondition = new Map<string, OverlapMark>(
    groups.flatMap((group) =>
      group.members.map(
        ({ conditionKey }) =>
          [conditionKey, { field: group.field, groupKey: group.key }] as const,
      ),
    ),
  );
  const sourcesOf = (group: OverlapGroup): OverlapSource[] =>
    group.members.flatMap(({ ruleKey, conditionKey }) => {
      const condition = conditionByKey.get(conditionKey);
      return condition ? [{ ruleKey, condition }] : [];
    });

  const revalidate = (touchedKeys: Set<FormRule["clientKey"]>) => {
    if (!latest.current.isSubmitted) return;
    const paths = getValues("rules").flatMap((rule, ruleIndex) =>
      touchedKeys.has(rule.clientKey)
        ? [`rules.${ruleIndex}.conditions` as const]
        : [],
    );
    if (paths.length > 0) void trigger(paths);
  };

  const restore = ({
    survivors,
    mergedAway,
    removedRules,
    countBefore,
  }: MergeSnapshot) => {
    if (!latest.current.enabled) return;
    if (
      getValues("rules").length + removedRules.length >
      Math.max(SEGMENT_LIMITS.rules, countBefore)
    ) {
      showToast(
        t("admin.segments.builder.limit.rules", {
          count: SEGMENT_LIMITS.rules,
        }),
        "warning",
      );
      return;
    }
    for (const rule of removedRules) {
      const currentKeys = getValues("rules").map((current) => current.clientKey);
      insertRule(restoreOrder.indexFor(rule.clientKey, currentKeys), rule);
    }
    const touchedKeys = new Set<FormRule["clientKey"]>();
    getValues("rules").forEach((rule, ruleIndex) => {
      const returning = mergedAway.filter(
        ({ ruleKey }) => ruleKey === rule.clientKey,
      );
      const hasSurvivor = rule.conditions.some(({ clientKey }) =>
        survivors.has(clientKey),
      );
      if (!hasSurvivor && returning.length === 0) return;
      const conditions = rule.conditions.map(
        (condition) => survivors.get(condition.clientKey) ?? condition,
      );
      for (const { condition } of returning) {
        const currentKeys = conditions.map(({ clientKey }) => clientKey);
        conditions.splice(
          restoreOrder.indexFor(condition.clientKey, currentKeys),
          0,
          condition,
        );
      }
      touchedKeys.add(rule.clientKey);
      setValue(`rules.${ruleIndex}.conditions`, conditions, {
        shouldDirty: true,
      });
    });
    revalidate(touchedKeys);
  };

  const showMergedToast = (snapshot: MergeSnapshot, count: number) =>
    showUndoToast({
      message: t("admin.segments.builder.overlap.mergedToast"),
      description: t("admin.segments.builder.overlap.mergedDescription", {
        count,
      }),
      onUndo: () => restore(snapshot),
    });

  const mergeAll = (): string[] => {
    if (!enabled) return [];
    const current = getValues("rules");
    const merging = findOverlapGroups(current, getValues("type"), fields);
    const { conditionsByRule, removedRules, removedIndexes, survivorKeys } =
      mergeOverlapGroups(current, merging);
    const survivorKeySet = new Set(survivorKeys);
    const snapshot: MergeSnapshot = {
      survivors: new Map(
        current.flatMap((rule) =>
          rule.conditions
            .filter(({ clientKey }) => survivorKeySet.has(clientKey))
            .map((condition) => [condition.clientKey, structuredClone(condition)] as const),
        ),
      ),
      mergedAway: current.flatMap((rule) => {
        const kept = conditionsByRule.get(rule.clientKey);
        if (!kept) return [];
        const keptKeys = new Set(kept.map(({ clientKey }) => clientKey));
        return rule.conditions
          .filter(({ clientKey }) => !keptKeys.has(clientKey))
          .map((condition) => ({
            ruleKey: rule.clientKey,
            condition: structuredClone(condition),
          }));
      }),
      removedRules: removedRules.map((rule) => structuredClone(rule)),
      countBefore: current.length,
    };
    restoreOrder.track(current.map((rule) => rule.clientKey));
    current.forEach((rule) =>
      restoreOrder.track(rule.conditions.map(({ clientKey }) => clientKey)),
    );
    current.forEach((rule, ruleIndex) => {
      const conditions = conditionsByRule.get(rule.clientKey);
      if (!conditions) return;
      setValue(`rules.${ruleIndex}.conditions`, conditions, {
        shouldDirty: true,
      });
    });
    if (removedIndexes.length > 0) removeRules(removedIndexes);
    revalidate(new Set(conditionsByRule.keys()));
    if (merging.length > 0) showMergedToast(snapshot, merging.length);
    return survivorKeys;
  };

  return {
    groups,
    overlapByCondition,
    sourcesOf,
    mergeAll,
  };
}
